import {
  FAQ_LIST,
  FAQ_CREATE,
  FAQ_UPDATE,
  FAQ_DELETE,
  FAQ_ATTACHMENT_UPLOAD,
  FAQ_FILE_UPLOAD,
  FAQ_SOURCES_LIST,
  FAQ_SOURCE_DOWNLOAD,
  FAQ_FILE_DELETE,
  FAQ_TAGS,
} from "../constants/url";
import httpBase from "../utils/http.utils";

let faqTagsCache = null;
let faqTagsInFlightRequest = null;

const normalizeFaqTagsPayload = (data = {}) => {
  const uniqueFaqTags = Array.from(
    new Set(
      (Array.isArray(data?.faq_tags) ? data.faq_tags : [])
        .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
        .filter(Boolean)
    )
  );

  const uniqueProducts = Array.from(
    new Set(
      (Array.isArray(data?.products) ? data.products : [])
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
    )
  );

  return {
    faq_tags: uniqueFaqTags,
    products: uniqueProducts,
  };
};

export const fetchFaqTagsOnce = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && faqTagsCache) {
    return faqTagsCache;
  }

  if (!forceRefresh && faqTagsInFlightRequest) {
    return faqTagsInFlightRequest;
  }

  faqTagsInFlightRequest = httpBase()
    .get(FAQ_TAGS)
    .then((response) => {
      const normalized = normalizeFaqTagsPayload(response?.data);
      faqTagsCache = normalized;
      return normalized;
    })
    .finally(() => {
      faqTagsInFlightRequest = null;
    });

  return faqTagsInFlightRequest;
};

export const listFaqsApi = ({
  query,
  is_verified,
  verified_by,
  source_id,
  tags,
  limit = 10,
  offset = 0,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async (dispatch) => {
    try {
      const response = await httpBase().get(FAQ_LIST, {
        params: { query, is_verified, verified_by, source_id, tags, limit, offset },
      });
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const updateFaqApi = ({
  faqId,
  data,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async (dispatch) => {
    try {
      const response = await httpBase().put(`${FAQ_UPDATE}${faqId}`, data);
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const createFaqApi = ({
  data,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async (dispatch) => {
    try {
      const response = await httpBase().post(FAQ_CREATE, data);
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const deleteFaqApi = ({
  faqId,
  faqIds,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async (dispatch) => {
    try {
      const normalizedIds = Array.isArray(faqIds)
        ? faqIds
        : faqId
        ? [faqId]
        : [];

      const response = await httpBase().delete(FAQ_DELETE, {
        data: {
          faq_ids: normalizedIds,
        },
      });
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const uploadFaqAttachmentApi = ({
  file,
  files,
  source = "faq",
  data = "",
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async (dispatch) => {
    try {
      const formData = new FormData();
      const normalizedFiles =
        Array.isArray(files) && files.length ? files : [file].filter(Boolean);

      normalizedFiles.forEach((currentFile) => {
        formData.append("files", currentFile);
      });

      const response = await httpBase().post(FAQ_ATTACHMENT_UPLOAD, formData, {
        params: { source, data },
      });
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const uploadFaqSourceFileApi = ({
  file,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async () => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await httpBase().post(FAQ_FILE_UPLOAD, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const listFaqSourcesApi = ({
  page = 1,
  limit = 5,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async () => {
    try {
      const response = await httpBase().get(FAQ_SOURCES_LIST, {
        params: { page, limit },
      });
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const getFaqSourceDownloadApi = ({
  sourceId,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async () => {
    try {
      const response = await httpBase().get(`${FAQ_SOURCE_DOWNLOAD}/${sourceId}`);
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const deleteFaqSourceFilesApi = ({
  sourceIds,
  deleteFaqs = false,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async () => {
    try {
      const response = await httpBase().delete(FAQ_FILE_DELETE, {
        data: {
          source_ids: sourceIds,
          delete_faqs: deleteFaqs,
        },
      });
      successCallback(response?.data);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};

export const getFaqTagsApi = ({
  forceRefresh = false,
  finalCallback,
  successCallback,
  failureCallback,
}) => {
  return async () => {
    try {
      const payload = await fetchFaqTagsOnce({ forceRefresh });
      successCallback(payload);
    } catch (e) {
      failureCallback(e);
    } finally {
      finalCallback();
    }
  };
};
